import React from 'react';
import { Form, Input, Select, Button, Spin } from 'antd';
import {
  SaveOutlined, CloseOutlined, DeleteOutlined, MessageOutlined,
  CalendarOutlined, ClockCircleOutlined, UserOutlined, QuestionCircleOutlined,
  AppstoreOutlined, CheckCircleOutlined,
} from '@ant-design/icons';
import GuardedButton from '../../components/guardedButton';
import { MdOutlineQueryBuilder } from "react-icons/md";

const { TextArea } = Input;

interface QueryDetailPanelProps {
  detail: any;
  loading: boolean;
  isEditing: boolean;
  canRespond: boolean;
  canDelete: boolean;
  saving: boolean;
  form: any;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onDelete: () => void;
}

const fmtDateTime = (v: any) =>
  v ? new Date(v).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : null;

const statusMeta = (status?: string): { cls: string; label: string } => {
  switch ((status || '').toUpperCase()) {
    case 'RESOLVED': return { cls: 'active', label: 'Resolved' };
    case 'RAISED': return { cls: 'pending', label: 'Raised' };
    default: return { cls: 'inactive', label: status || '—' };
  }
};

const personName = (p: any) => (typeof p === 'object' && p !== null ? (p.name ?? `${p.firstName ?? ''}${p.lastName ? ' ' + p.lastName : ''}`.trim()) : p);

const Field: React.FC<{ icon?: React.ReactNode; label: string; value: React.ReactNode }> = ({ icon, label, value }) => {
  const isEmpty = value === null || value === undefined || value === '';
  return (
    <div className="cat-field">
      <span className="cat-field-label">{icon}{label}</span>
      <span className="cat-field-value">{isEmpty ? <span className="cat-muted">—</span> : value}</span>
    </div>
  );
};

const QueryDetailPanel: React.FC<QueryDetailPanelProps> = ({
  detail, loading, isEditing, canRespond, canDelete, saving, form,
  onEdit, onCancel, onSave, onDelete,
}) => {
  const status = statusMeta(detail?.status);
  const createdBy = detail?.createdByName ?? personName(detail?.createdBy);
  const respondedBy = detail?.resolvedByName ?? personName(detail?.resolvedBy);
  const hasResponse = Boolean(detail?.remarks);

  const statusPill = (
    <span className={`batch-status-pill batch-status-pill--${status.cls}`} style={{ border: '1px solid #77797a' }}>
      <span className="batch-status-dot" />{status.label}
    </span>
  );

  return (
    <div className="cat-panel">
      <div className="cat-card">
        <div className="cat-card-head">
          <div className="cat-head-left">
            <div className="cat-head-icon"><MdOutlineQueryBuilder size={35} /></div>
            <div className="cat-head-titles">
              <div className="cat-head-name">{'Query'}</div>
              <div className="cat-head-meta">
                {detail?.createdAt && <span className="cat-head-code"><CalendarOutlined /> {fmtDateTime(detail.createdAt)}</span>}
              </div>
            </div>
          </div>

          <div className="cat-head-actions" onClick={(e) => e.stopPropagation()}>
            {!isEditing ? (
              <>
                {canRespond && (
                  <GuardedButton className="cat-btn cat-btn-edit" icon={<MessageOutlined />} onClick={onEdit}>Respond</GuardedButton>
                )}
             
              </>
            ) : (
              <>
                <Button className="cat-btn" icon={<CloseOutlined />} onClick={onCancel}>Cancel</Button>
                <Button className="cat-btn" type="primary" icon={<SaveOutlined />} loading={saving} onClick={onSave}>Save Response</Button>
              </>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="cat-card-body">
          {loading ? (
            <div className="cat-loading"><Spin /></div>
          ) : (
            <>
              {/* Detail grid */}
              <div className="cat-section" style={{ padding: '16px 0 16px' }}>
                <div className="cat-grid cat-grid--3">
                  <Field
                    icon={<AppstoreOutlined />}
                    label="Category :"
                    value={detail?.categoryName ? <span className="cat-chip" style={{ background: '#e6f4ff', color: '#1677ff', border: '1px solid #77797a', borderRadius: '16px' }}>{detail.categoryName}</span> : null}
                  />
                  <Field icon={<CheckCircleOutlined />} label="Status :" value={statusPill} />
                  <Field icon={<UserOutlined />} label="Raised By :" value={createdBy} />
                  <Field icon={<CalendarOutlined />} label="Created At :" value={fmtDateTime(detail?.createdAt)} />
                  <Field icon={<UserOutlined />} label="Responded By :" value={respondedBy} />
                  <Field icon={<ClockCircleOutlined />} label="Updated At :" value={fmtDateTime(detail?.updatedAt)} />
                </div>
              </div>

              {isEditing ? (
                <Form form={form} layout="vertical" className="cat-form qd-form">
                  {/* Original question shown for context */}
                  <div className="qd-thread">
                    <div className="qd-msg">
                      <div className="qd-msg-avatar qd-msg-avatar--q"><QuestionCircleOutlined /></div>
                      <div className="qd-msg-body">
                        <div className="qd-msg-head">
                          <span className="qd-msg-author">{createdBy || 'User'}</span>
                          <span className="qd-msg-role">raised an issue</span>
                          {detail?.createdAt && <span className="qd-msg-time">{fmtDateTime(detail.createdAt)}</span>}
                        </div>
                        <div className="qd-bubble qd-bubble--issue">{detail?.message || '—'}</div>
                      </div>
                    </div>
                  </div>

                  <div className="qd-reply-box">
                    <div className="qd-reply-head">
                      <span className="qd-reply-title"><MessageOutlined /> Compose Response</span>
                    </div>
                    <div className="qd-reply-grid">
                      <Form.Item name="status" label="Mark as">
                        <Select
                          options={[
                            { label: 'Raised', value: 'RAISED' },
                            { label: 'Resolved', value: 'RESOLVED' },
                          ]}
                        />
                      </Form.Item>
                    </div>
                    <Form.Item
                      name="remarks"
                      label="Your Response"
                      rules={[{ required: true, message: 'Please enter a response' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <TextArea rows={5} placeholder="Type your response to this query..." />
                    </Form.Item>
                  </div>
                </Form>
              ) : (
                <div className="qd-thread">
                  {/* Issue message */}
                  <div className="qd-msg">
                    <div className="qd-msg-avatar qd-msg-avatar--q"><QuestionCircleOutlined /></div>
                    <div className="qd-msg-body">
                      <div className="qd-msg-head">
                        <span className="qd-msg-author">{createdBy || 'User'}</span>
                        <span className="qd-msg-role">raised an issue</span>
                        {detail?.createdAt && <span className="qd-msg-time">{fmtDateTime(detail.createdAt)}</span>}
                      </div>
                      <div className="qd-bubble qd-bubble--issue">
                        {detail?.message || <span className="cat-muted">—</span>}
                      </div>
                    </div>
                  </div>

                  {/* Response message */}
                  <div className="qd-msg qd-msg--response">
                    <div className={`qd-msg-avatar ${hasResponse ? 'qd-msg-avatar--r' : 'qd-msg-avatar--wait'}`}>
                      {hasResponse ? <MessageOutlined /> : <ClockCircleOutlined />}
                    </div>
                    <div className="qd-msg-body">
                      <div className="qd-msg-head">
                        <span className="qd-msg-author">{hasResponse ? (respondedBy || 'Support') : 'Support Team'}</span>
                        <span className="qd-msg-role">{hasResponse ? 'responded' : 'pending'}</span>
                        {hasResponse && detail?.updatedAt && <span className="qd-msg-time">{fmtDateTime(detail.updatedAt)}</span>}
                      </div>
                      <div className={`qd-bubble qd-bubble--response${hasResponse ? '' : ' qd-bubble--wait'}`}>
                        {hasResponse ? detail.remarks : 'Awaiting response from the support team'}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QueryDetailPanel;
